/**
 * Native (Capacitor) bootstrap.
 *
 * Runs once on app start. Safely no-ops on the web build so the exact same
 * bundle can be shipped to both app.futureself.es and the App/Play stores.
 *
 * Responsibilities:
 *  1. Configure the native status bar to match FutureSelf's dark theme.
 *  2. Wire the Android hardware Back button — modals get first refusal,
 *     then react-router history, then a graceful exit.
 *  3. Broadcast app foreground/background events (`fs:app-inactive` /
 *     `fs:app-active`) so audio-heavy screens can pause on interruptions
 *     (incoming call, screen off, home button) and resume cleanly.
 *  4. Expose a boolean the rest of the app can read (isNativePlatform).
 */

import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';

export const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/* ---------- Back-button handler stack ----------
 * Components (modals, overlays, immersive players) can register a temporary
 * interceptor via `pushBackHandler(fn)`. The most recently pushed handler
 * runs first on Android hardware Back. If it returns `true` the event is
 * considered consumed. Handlers MUST call the returned unsubscribe fn on
 * unmount so we never trap the user. */
const _backStack = [];

export const pushBackHandler = (fn) => {
  if (typeof fn !== 'function') return () => {};
  _backStack.push(fn);
  return () => {
    const idx = _backStack.lastIndexOf(fn);
    if (idx >= 0) _backStack.splice(idx, 1);
  };
};

const _runBackHandlers = () => {
  // Iterate from most recently pushed downwards.
  for (let i = _backStack.length - 1; i >= 0; i -= 1) {
    try {
      if (_backStack[i]() === true) return true;
    } catch { /* swallow – never brick back button */ }
  }
  return false;
};

export const initNativeShell = async () => {
  if (!isNativePlatform()) return;

  // ---- Status bar: dark theme, light-on-dark content ----
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({ color: '#1C1C1E' });
    }
    // Web content should render BELOW the status bar, not underneath it.
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch (err) {
    // Non-fatal – older devices may not support every StatusBar call.
    console.warn('[native] StatusBar init:', err?.message || err);
  }

  // ---- Android back-button: modal-aware, then history, then graceful exit ----
  try {
    CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      // 1. Give any registered overlay/modal the chance to consume it.
      if (_runBackHandlers()) return;
      // 2. Fall back to router history.
      if (canGoBack) {
        window.history.back();
        return;
      }
      // 3. At the top of the stack – exit gracefully instead of trapping the user.
      CapacitorApp.exitApp();
    });
  } catch (err) {
    console.warn('[native] backButton wiring:', err?.message || err);
  }

  // ---- App state → global events for audio-heavy screens ----
  try {
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      const name = isActive ? 'fs:app-active' : 'fs:app-inactive';
      window.dispatchEvent(new CustomEvent(name));
    });
  } catch (err) {
    console.warn('[native] appStateChange wiring:', err?.message || err);
  }
};
